import { useSyncExternalStore } from "react";
import type { User } from "../api/types";
import { readStore, writeStore } from "../lib/storage";

/** The signed-in session (§16.5: token kept in persistent client storage).
 *
 * An impersonation session (handed over by pos-admin, §15.1) lives in sessionStorage, apart
 * from the tenant's own session in localStorage, so it never overwrites a real sign-in on
 * this device and it ends with the tab. The active session is the impersonation if any. */

export type Session = { token: string; user: User };
export type Impersonation = Session & { returnUrl: string | null; actorName: string };

const SESSION_KEY = "pos.session";
const IMPERSONATION_KEY = "pos.impersonation";

const isSession = (v: unknown): v is Session => !!v && typeof v === "object" && typeof (v as Session).token === "string" && !!(v as Session).user && typeof (v as Session).user.userId === "number";
const isImpersonation = (v: unknown): v is Impersonation => isSession(v) && typeof (v as Impersonation).actorName === "string";

type Snapshot = { session: Session | null; impersonation: Impersonation | null };
let snap: Snapshot = {
  session: readStore("local", SESSION_KEY, null, (v): v is Session | null => v === null || isSession(v)),
  impersonation: readStore("session", IMPERSONATION_KEY, null, (v): v is Impersonation | null => v === null || isImpersonation(v)),
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const sessionStore = {
  get: (): Snapshot => snap,
  active: (): Session | null => snap.impersonation ?? snap.session,
  token: (): string | null => (snap.impersonation ?? snap.session)?.token ?? null,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  setSession(s: Session | null): void {
    snap = { ...snap, session: s };
    writeStore("local", SESSION_KEY, s);
    emit();
  },
  setImpersonation(s: Impersonation | null): void {
    snap = { ...snap, impersonation: s };
    writeStore("session", IMPERSONATION_KEY, s);
    emit();
  },
  /** Replace the active session's user (after /auth/me or a password change). */
  updateUser(patch: Partial<User>): void {
    if (snap.impersonation) this.setImpersonation({ ...snap.impersonation, user: { ...snap.impersonation.user, ...patch } });
    else if (snap.session) this.setSession({ ...snap.session, user: { ...snap.session.user, ...patch } });
  },
  clearActive(): void {
    if (snap.impersonation) this.setImpersonation(null);
    else this.setSession(null);
  },
};

// Another tab signed in or out: follow it (only the persistent session is shared).
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== SESSION_KEY) return;
    snap = { ...snap, session: readStore("local", SESSION_KEY, null, (v): v is Session | null => v === null || isSession(v)) };
    emit();
  });
}

export function useSessionSnapshot(): Snapshot {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get, sessionStore.get);
}
